import { Transform } from 'types/common';

type Entry = any[];
type AnyFunction = (e: any) => any;

/**
 * Execute the function with each value
 * @param {Function} fn
 * @example
 * executeWithSpreadedArray((v) => v*2)(1, 2) = [2, 4]
 */
export const executeWithSpreadArray = (fn: AnyFunction) => (...arr: any[]) => arr.map(fn);

/**
 * Expose three functions
 *
 * `.keys`: Execute the function with each key of object
 *
 * `.values`: Execute the function with each value in object
 *
 * `.entries`: Execute the function with each entry in object
 *
 * @example
 * executeWithObject.keys((v) => v + v)({a: 1, b: 2}) = {a: 'aa', b: 'bb'}
 *
 * @example
 * executeWithObject.values((v) => v*2)({a: 1, b: 2}) = {a: 2, b: 4}
 *
 * @example
 * executeWithObject.entries(([k, v]) => [k, v*2])({a: 1, b: 2}) = {a: 2, b: 4}
 */
export const executeWithObject = (() => {
  const withKey = (fn: AnyFunction) => ([k]: Entry) => fn(k);
  const withVal = (fn: AnyFunction) => ([, v]: Entry) => fn(v);
  const withEntry = (fn: AnyFunction) => ([k, v]: Entry) => fn([k, v]);
  const toEntry = (fn: AnyFunction) => ([k, v]: Entry) => [k, fn([k, v])];

  const execute = (hof: AnyFunction) => (fn: AnyFunction) => <T, R = unknown>(obj: T): Transform<T, R> => {
    try {
      return Object.fromEntries(Object.entries(obj).map(toEntry(hof(fn))));
    } catch (err) {
      // Ignore
    }

    return <Transform<T, R>>(<unknown>obj);
  };

  return {
    keys: execute(withKey),
    values: execute(withVal),
    entries: execute(withEntry),
  };
})();

export const formatNumber = (value: number, options?: Intl.NumberFormatOptions): string =>
  new Intl.NumberFormat('en-US', options).format(value);
